import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchGlobalSettings } from '../api/globalsetting';

const sanitizeString = (value) => {
    if (typeof value !== "string") return "";
    const trimmed = value.trim();
    return trimmed.length ? trimmed : "";
};

const sanitizeImage = (image) => {
    if (!image) return null;
    
    // For OG images, prioritize larger sizes for better social media sharing
    // Priority: 2048x2048 > large > 1536x1536 > medium_large > original url
    let ogImageUrl = null;
    
    // Check sizes object - handle both bracket notation and dot notation
    if (image.sizes) {
        ogImageUrl = 
            sanitizeString(image.sizes['2048x2048']) ||
            sanitizeString(image.sizes.large) ||
            sanitizeString(image.sizes['1536x1536']) ||
            sanitizeString(image.sizes.medium_large) ||
            null;
    }
    
    // Fallback to direct url if no size found
    if (!ogImageUrl) {
        ogImageUrl = sanitizeString(image.url || "");
    }
    
    // Ensure absolute URL for OG images (required by social media platforms)
    if (ogImageUrl && !ogImageUrl.startsWith('http://') && !ogImageUrl.startsWith('https://')) {
        // If relative URL, make it absolute
        ogImageUrl = window.location.origin + (ogImageUrl.startsWith('/') ? ogImageUrl : '/' + ogImageUrl);
    }
    
    if (!ogImageUrl) return null;
    return { url: ogImageUrl, alt: sanitizeString(image?.alt || "") };
};

// Helper function to get or create meta tag
const getOrCreateMetaTag = (attribute, value, content) => {
    let meta = document.querySelector(`meta[${attribute}="${value}"]`);
    if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, value);
        document.head.appendChild(meta);
    }
    if (content) {
        meta.setAttribute('content', content);
    }
    return meta;
};

// Helper function to remove meta tags by attribute
const removeMetaTags = (attribute, value) => {
    const tags = document.querySelectorAll(`meta[${attribute}="${value}"]`);
    tags.forEach(tag => tag.remove());
};

function DocumentHead({ pageSeo = null }) {
    const location = useLocation();
    const [globalSettings, setGlobalSettings] = useState(null);

    // Fetch global settings once on mount
    useEffect(() => {
        let isMounted = true;

        const fetchSettings = async () => {
            try {
                const apiData = await fetchGlobalSettings();
                if (isMounted) {
                    setGlobalSettings(apiData);
                }
            } catch (error) {
                // Ignored: meta tags still work without global settings.
            }
        };

        fetchSettings();

        return () => {
            isMounted = false;
        };
    }, []);

    // Update meta tags whenever pageSeo or globalSettings changes
    useEffect(() => {
        if (!globalSettings) return; // Wait for global settings to load

        const apiData = globalSettings;
        
        // Get current URL
        const currentUrl = window.location.origin + location.pathname;

        // Update document title - prioritize page SEO, then global settings
        const pageTitle = sanitizeString(pageSeo?.title || "");
        const siteTitle = sanitizeString(
            apiData?.site_title || 
            apiData?.title || 
            apiData?.site_name ||
            apiData?.name ||
            ""
        );
        const finalTitle = pageTitle || siteTitle;
        
        if (finalTitle) {
            document.title = finalTitle;
        }

        // Update meta description - prioritize page SEO, then global settings
        const pageDescription = sanitizeString(pageSeo?.description || "");
        const tagline = sanitizeString(
            apiData?.tagline || 
            apiData?.description || 
            apiData?.site_description ||
            apiData?.meta_description ||
            ""
        );
        const finalDescription = pageDescription || tagline;
        
        if (finalDescription) {
            getOrCreateMetaTag('name', 'description', finalDescription);
        }

        // Update canonical URL - prioritize page SEO canonical
        const pageCanonical = sanitizeString(pageSeo?.canonical || "");
        
        if (pageCanonical) {
            // Remove existing canonical links
            const existingCanonicals = document.querySelectorAll('link[rel="canonical"]');
            existingCanonicals.forEach(link => link.remove());
            
            // Add new canonical link
            const canonicalLink = document.createElement('link');
            canonicalLink.setAttribute('rel', 'canonical');
            canonicalLink.setAttribute('href', pageCanonical);
            document.head.appendChild(canonicalLink);
        }

        // Update robots meta tag - prioritize page SEO robots
        const pageRobots = sanitizeString(pageSeo?.robots || "");
        if (pageRobots) {
            getOrCreateMetaTag('name', 'robots', pageRobots);
        } else {
            // Default to index,follow if not specified
            getOrCreateMetaTag('name', 'robots', 'index,follow');
        }

        // Update favicon - check multiple possible field names and nested structures
        const favicon = sanitizeImage(
            apiData?.favicon || 
            apiData?.site_favicon ||
            apiData?.site_icon ||
            apiData?.icon ||
            apiData?.site_settings?.favicon ||
            apiData?.general?.favicon ||
            null
        );
        if (favicon?.url) {
            // Remove existing favicon links
            const existingFavicons = document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]');
            existingFavicons.forEach(link => link.remove());

            // Add new favicon
            const link = document.createElement('link');
            link.rel = 'icon';
            // Try to detect image type
            const faviconUrl = favicon.url.toLowerCase();
            if (faviconUrl.includes('.png')) {
                link.type = 'image/png';
            } else if (faviconUrl.includes('.svg')) {
                link.type = 'image/svg+xml';
            } else {
                link.type = 'image/x-icon';
            }
            link.href = favicon.url;
            document.head.appendChild(link);

            // Also update apple-touch-icon if available
            let appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]');
            if (!appleTouchIcon) {
                appleTouchIcon = document.createElement('link');
                appleTouchIcon.setAttribute('rel', 'apple-touch-icon');
                document.head.appendChild(appleTouchIcon);
            }
            appleTouchIcon.setAttribute('href', favicon.url);
        }

        // Get social media image (og_image, social_image, or fallback to favicon)
        const ogImage = sanitizeImage(
            apiData?.og_image || 
            apiData?.social_image || 
            apiData?.share_image ||
            apiData?.og_image_url ||
            apiData?.site_settings?.og_image ||
            apiData?.general?.og_image ||
            (favicon?.url ? { url: favicon.url } : null)
        );

        // Open Graph Meta Tags - prioritize page SEO OG data
        const ogTitle = sanitizeString(pageSeo?.og?.title || "") || finalTitle;
        const ogDescription = sanitizeString(pageSeo?.og?.description || "") || finalDescription;
        const ogImageUrl = sanitizeString(pageSeo?.og?.image || "");
        
        if (ogTitle) {
            getOrCreateMetaTag('property', 'og:title', ogTitle);
        }
        if (ogDescription) {
            getOrCreateMetaTag('property', 'og:description', ogDescription);
        }
        
        // Handle OG image - prioritize page SEO image, then global
        if (ogImageUrl) {
            // Add cache-busting parameter to help with social media cache refresh
            const finalOgImageUrl = ogImageUrl.includes('?') 
                ? `${ogImageUrl}&v=${Date.now()}` 
                : `${ogImageUrl}?v=${Date.now()}`;
            
            getOrCreateMetaTag('property', 'og:image', finalOgImageUrl);
            
            // Remove generic image dimensions for page-specific images
            // (they should be from API if available)
            if (pageSeo?.og?.image_width) {
                getOrCreateMetaTag('property', 'og:image:width', String(pageSeo.og.image_width));
            }
            if (pageSeo?.og?.image_height) {
                getOrCreateMetaTag('property', 'og:image:height', String(pageSeo.og.image_height));
            }
        } else if (ogImage?.url) {
            // Fallback to global OG image
            const ogImageUrlFallback = ogImage.url.includes('?') 
                ? `${ogImage.url}&v=${Date.now()}` 
                : `${ogImage.url}?v=${Date.now()}`;
            
            getOrCreateMetaTag('property', 'og:image', ogImageUrlFallback);
            
            // Add image dimensions if available (helps with social media recognition)
            if (apiData?.og_image?.width) {
                getOrCreateMetaTag('property', 'og:image:width', String(apiData.og_image.width));
            }
            if (apiData?.og_image?.height) {
                getOrCreateMetaTag('property', 'og:image:height', String(apiData.og_image.height));
            }
            if (ogImage.alt) {
                getOrCreateMetaTag('property', 'og:image:alt', ogImage.alt);
            }
        }
        
        getOrCreateMetaTag('property', 'og:url', pageCanonical || currentUrl);
        getOrCreateMetaTag('property', 'og:type', 'website');
        if (siteTitle) {
            getOrCreateMetaTag('property', 'og:site_name', siteTitle);
        }

        // Twitter Card Meta Tags - use same data as Open Graph
        getOrCreateMetaTag('name', 'twitter:card', 'summary_large_image');
        if (ogTitle) {
            getOrCreateMetaTag('name', 'twitter:title', ogTitle);
        }
        if (ogDescription) {
            getOrCreateMetaTag('name', 'twitter:description', ogDescription);
        }
        if (ogImageUrl) {
            getOrCreateMetaTag('name', 'twitter:image', ogImageUrl);
        } else if (ogImage?.url) {
            getOrCreateMetaTag('name', 'twitter:image', ogImage.url);
            if (ogImage.alt) {
                getOrCreateMetaTag('name', 'twitter:image:alt', ogImage.alt);
            }
        }

        // Additional meta tags
        getOrCreateMetaTag('name', 'viewport', 'width=device-width, initial-scale=1');
    }, [pageSeo, globalSettings, location.pathname]); // Run when pageSeo, globalSettings, or pathname changes

    return null; // This component doesn't render anything
}

export default DocumentHead;

